import { useState, useEffect, useCallback } from 'react';
import { AdminUser, AdminHealthResponse, AdminInfoResponse } from '../types';
import { adminAuthService } from '../services/adminAuth';
import { adminApiService } from '../services/adminApi';
import { ApiError } from '../services/api';
import { useAuth } from '../contexts/AppKitAuthContext';

interface AdminState {
  isAdmin: boolean;
  user: AdminUser | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface AdminActions {
  logout: () => void;
  clearError: () => void;
}

interface SystemData {
  health: AdminHealthResponse | null;
  systemInfo: AdminInfoResponse | null;
  healthLoading: boolean;
  systemLoading: boolean;
  healthError: string | null;
  systemError: string | null;
}

interface JobData {
  streakResetLoading: boolean;
  streakResetError: string | null;
  streakResetSuccess: string | null;
  drawCompletionLoading: boolean;
  drawCompletionError: string | null;
  drawCompletionSuccess: string | null;
  leaderboardSnapshotLoading: boolean;
  leaderboardSnapshotError: string | null;
  leaderboardSnapshotSuccess: string | null;
}

interface SystemActions {
  refreshHealth: () => Promise<void>;
  refreshSystemInfo: () => Promise<void>;
  clearHealthError: () => void;
  clearSystemError: () => void;
}

interface JobActions {
  triggerStreakReset: () => Promise<void>;
  clearStreakResetError: () => void;
  clearStreakResetSuccess: () => void;
  triggerDrawCompletion: (date?: string) => Promise<void>;
  clearDrawCompletionError: () => void;
  clearDrawCompletionSuccess: () => void;
  triggerLeaderboardSnapshot: (weekStartDate?: string) => Promise<void>;
  clearLeaderboardSnapshotError: () => void;
  clearLeaderboardSnapshotSuccess: () => void;
}

export function useAdmin(): AdminState & AdminActions & SystemData & SystemActions & JobData & JobActions {
  const { isAuthenticated, userInfo, isLoading, isInitializing } = useAuth();
  
  const [state, setState] = useState<AdminState>({
    isAdmin: false,
    user: null,
    loading: true,
    error: null,
    initialized: false,
  });

  const [systemData, setSystemData] = useState<SystemData>({
    health: null,
    systemInfo: null,
    healthLoading: false,
    systemLoading: false,
    healthError: null,
    systemError: null,
  });

  const [jobData, setJobData] = useState<JobData>({
    streakResetLoading: false,
    streakResetError: null,
    streakResetSuccess: null,
    drawCompletionLoading: false,
    drawCompletionError: null,
    drawCompletionSuccess: null,
    leaderboardSnapshotLoading: false,
    leaderboardSnapshotError: null,
    leaderboardSnapshotSuccess: null,
  });

  // Update admin status based on auth context
  useEffect(() => {
    const updateAdminStatus = async () => {
      try {
        // Don't proceed if auth is still loading or initializing
        if (isLoading || isInitializing) {
          console.log('Admin check: Auth still loading/initializing', { isLoading, isInitializing });
          setState(prev => ({ ...prev, loading: true, initialized: false }));
          return;
        }

        setState(prev => ({ ...prev, loading: true, error: null }));
        
        console.log('Admin check:', { 
          isAuthenticated, 
          userInfo: userInfo, 
          isAdmin: userInfo?.isAdmin,
          isLoading,
          isInitializing 
        });
        
        if (isAuthenticated && userInfo?.isAdmin) {
          // Initialize admin auth service with the authenticated user's token
          await adminAuthService.initialize();
          
          const user = adminAuthService.getCurrentUser();
          
          console.log('Setting admin state to true');
          setState({
            isAdmin: true,
            user: user ? { ...user, isAdmin: true } : { 
              walletAddress: userInfo.walletAddress, 
              isAdmin: true 
            },
            loading: false,
            error: null,
            initialized: true,
          });
        } else {
          console.log('Setting admin state to false');
          setState({
            isAdmin: false,
            user: null,
            loading: false,
            error: null,
            initialized: true,
          });
        }
      } catch (error) {
        console.log('Admin check error:', error);
        setState({
          isAdmin: false,
          user: null,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to initialize admin',
          initialized: true,
        });
      }
    };

    updateAdminStatus();
  }, [isAuthenticated, userInfo, isLoading, isInitializing]);


  // Logout
  const logout = useCallback((): void => {
    adminAuthService.logout();
    setState({
      isAdmin: false,
      user: null,
      loading: false,
      error: null,
      initialized: true,
    });
    setSystemData({
      health: null,
      systemInfo: null,
      healthLoading: false,
      systemLoading: false,
      healthError: null,
      systemError: null,
    });
  }, []);

  // Clear error
  const clearError = useCallback((): void => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Refresh health data
  const refreshHealth = useCallback(async (): Promise<void> => {
    if (!state.isAdmin) return;

    try {
      setSystemData(prev => ({ ...prev, healthLoading: true, healthError: null }));
      const health = await adminApiService.getHealth();
      setSystemData(prev => ({ ...prev, health, healthLoading: false }));
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to fetch health data';
      setSystemData(prev => ({ 
        ...prev, 
        healthError: errorMessage, 
        healthLoading: false 
      }));
    }
  }, [state.isAdmin]);

  // Refresh system info
  const refreshSystemInfo = useCallback(async (): Promise<void> => {
    if (!state.isAdmin) return;

    try {
      setSystemData(prev => ({ ...prev, systemLoading: true, systemError: null }));
      const systemInfo = await adminApiService.getSystemInfo();
      setSystemData(prev => ({ ...prev, systemInfo, systemLoading: false }));
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to fetch system info';
      setSystemData(prev => ({ 
        ...prev, 
        systemError: errorMessage, 
        systemLoading: false 
      }));
    }
  }, [state.isAdmin]);

  // Clear health error
  const clearHealthError = useCallback((): void => {
    setSystemData(prev => ({ ...prev, healthError: null }));
  }, []);

  // Clear system error
  const clearSystemError = useCallback((): void => {
    setSystemData(prev => ({ ...prev, systemError: null }));
  }, []);

  // Trigger streak reset
  const triggerStreakReset = useCallback(async (): Promise<void> => {
    if (!state.isAdmin) {
      throw new ApiError('Admin access required', 403, 'ADMIN_ACCESS_REQUIRED');
    }

    setJobData(prev => ({ 
      ...prev, 
      streakResetLoading: true, 
      streakResetError: null, 
      streakResetSuccess: null 
    }));

    try {
      const result = await adminApiService.triggerStreakReset();
      setJobData(prev => ({ 
        ...prev, 
        streakResetLoading: false,
        streakResetSuccess: result.message
      }));
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setJobData(prev => ({ ...prev, streakResetSuccess: null }));
      }, 5000);
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to reset streaks';
      setJobData(prev => ({ 
        ...prev, 
        streakResetLoading: false, 
        streakResetError: errorMessage 
      }));
    }
  }, [state.isAdmin]);

  // Clear streak reset error
  const clearStreakResetError = useCallback((): void => {
    setJobData(prev => ({ ...prev, streakResetError: null }));
  }, []);

  // Clear streak reset success
  const clearStreakResetSuccess = useCallback((): void => {
    setJobData(prev => ({ ...prev, streakResetSuccess: null }));
  }, []);

  // Trigger draw completion
  const triggerDrawCompletion = useCallback(async (date?: string): Promise<void> => {
    if (!state.isAdmin) {
      throw new ApiError('Admin access required', 403, 'ADMIN_ACCESS_REQUIRED');
    }

    setJobData(prev => ({ 
      ...prev, 
      drawCompletionLoading: true, 
      drawCompletionError: null, 
      drawCompletionSuccess: null 
    }));

    try {
      const result = await adminApiService.triggerDrawCompletion(date);
      setJobData(prev => ({ 
        ...prev, 
        drawCompletionLoading: false,
        drawCompletionSuccess: result.message
      }));
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setJobData(prev => ({ ...prev, drawCompletionSuccess: null }));
      }, 5000);
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to complete draw';
      setJobData(prev => ({ 
        ...prev, 
        drawCompletionLoading: false, 
        drawCompletionError: errorMessage 
      }));
    }
  }, [state.isAdmin]);

  // Clear draw completion error
  const clearDrawCompletionError = useCallback((): void => {
    setJobData(prev => ({ ...prev, drawCompletionError: null }));
  }, []);

  // Clear draw completion success
  const clearDrawCompletionSuccess = useCallback((): void => {
    setJobData(prev => ({ ...prev, drawCompletionSuccess: null }));
  }, []);

  // Trigger leaderboard snapshot
  const triggerLeaderboardSnapshot = useCallback(async (weekStartDate?: string): Promise<void> => {
    if (!state.isAdmin) {
      throw new ApiError('Admin access required', 403, 'ADMIN_ACCESS_REQUIRED');
    }

    setJobData(prev => ({ 
      ...prev, 
      leaderboardSnapshotLoading: true, 
      leaderboardSnapshotError: null, 
      leaderboardSnapshotSuccess: null 
    }));

    try {
      const result = await adminApiService.generateLeaderboardSnapshot(weekStartDate);
      setJobData(prev => ({ 
        ...prev, 
        leaderboardSnapshotLoading: false,
        leaderboardSnapshotSuccess: result.message
      }));
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setJobData(prev => ({ ...prev, leaderboardSnapshotSuccess: null }));
      }, 5000);
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to generate leaderboard snapshot';
      setJobData(prev => ({ 
        ...prev, 
        leaderboardSnapshotLoading: false, 
        leaderboardSnapshotError: errorMessage 
      }));
    }
  }, [state.isAdmin]);

  // Clear leaderboard snapshot error
  const clearLeaderboardSnapshotError = useCallback((): void => {
    setJobData(prev => ({ ...prev, leaderboardSnapshotError: null }));
  }, []);

  // Clear leaderboard snapshot success
  const clearLeaderboardSnapshotSuccess = useCallback((): void => {
    setJobData(prev => ({ ...prev, leaderboardSnapshotSuccess: null }));
  }, []);

  // Auto-refresh health and system data when admin access is confirmed
  useEffect(() => {
    if (state.isAdmin && state.initialized) {
      refreshHealth();
      refreshSystemInfo();
    }
  }, [state.isAdmin, state.initialized, refreshHealth, refreshSystemInfo]);

  return {
    ...state,
    ...systemData,
    ...jobData,
    logout,
    clearError,
    refreshHealth,
    refreshSystemInfo,
    clearHealthError,
    clearSystemError,
    triggerStreakReset,
    clearStreakResetError,
    clearStreakResetSuccess,
    triggerDrawCompletion,
    clearDrawCompletionError,
    clearDrawCompletionSuccess,
    triggerLeaderboardSnapshot,
    clearLeaderboardSnapshotError,
    clearLeaderboardSnapshotSuccess,
  };
}